import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import type { AdminPasswordReset } from '@/services/user-management.service';

interface TemporaryPasswordDialogProps {
  result: AdminPasswordReset | null;
  employeeName: string;
  onClose: () => void;
}

/**
 * Shows the temporary password produced by an admin-initiated reset.
 *
 * The password exists in plaintext only inside this dialog: the API returns it
 * once and stores only a hash, so there is no way to retrieve it again. The
 * copy affordance and the explicit warning both exist because an administrator
 * who dismisses this without noting the password has to perform a second reset.
 *
 * The username and employee code are shown alongside it deliberately — under
 * D-074 an employee may share an email address with colleagues, so the
 * administrator needs an unambiguous identifier when passing the credential on.
 */
export function TemporaryPasswordDialog({
  result,
  employeeName,
  onClose,
}: TemporaryPasswordDialogProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be blocked (insecure origin, permissions policy).
      // The password stays visible and selectable, so this is not fatal.
      setCopied(false);
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Modal
      open={result !== null}
      onClose={handleClose}
      title="Temporary Password Created"
      size="md"
    >
      {result && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            A temporary password has been created for <strong>{employeeName}</strong>. They will be
            required to choose a new password the next time they sign in.
          </p>

          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 dark:border-amber-700/50 dark:bg-amber-900/15">
            <p className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
              />
              <span>
                This password is shown once and cannot be retrieved later. Copy it now — if it is
                lost, you will need to reset the password again.
              </span>
            </p>
          </div>

          <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs font-medium text-muted-foreground">Username</span>
              <span className="font-mono text-sm text-foreground">{result.username ?? '—'}</span>
            </div>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs font-medium text-muted-foreground">Employee Code</span>
              <span className="font-mono text-sm text-foreground">
                {result.employeeCode ?? '—'}
              </span>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              Temporary Password
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 select-all break-all rounded-lg border border-border bg-background px-3 py-2.5 font-mono text-sm text-foreground">
                {result.temporaryPassword}
              </code>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleCopy()}
                className="min-h-[44px] shrink-0"
                aria-label="Copy temporary password"
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="h-4 w-4" />
                <span className="ml-2 hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
              </Button>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <Button type="button" onClick={handleClose} className="min-h-[44px]">
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
